// Inline formatting from the selection toolbar: bold and italic through the
// browser's own commands (they write <b> and <i>, which lib/sanitize.ts keeps),
// and the author's highlight as a bare <mark>. The reader's own marks (lines a
// reader took) carry data-take-mark and are added after sanitising, so the two
// never mix.

export interface FormatState {
  bold: boolean
  italic: boolean
  highlight: boolean
}

const markOf = (n: Node | null, host: HTMLElement): HTMLElement | null => {
  const el = n && (n.nodeType === Node.ELEMENT_NODE ? (n as Element) : n.parentElement)
  const m = el?.closest('mark')
  return m && host.contains(m) ? (m as HTMLElement) : null
}

function commandState(name: 'bold' | 'italic'): boolean {
  try {
    return typeof document.queryCommandState === 'function' && document.queryCommandState(name)
  } catch {
    return false
  }
}

/** What's on for the selected words in `host`. */
export function formatState(range: Range, host: HTMLElement): FormatState {
  return {
    bold: commandState('bold'),
    italic: commandState('italic'),
    highlight: isHighlighted(range, host),
  }
}

/** Every bit of selected text sits inside a <mark>. */
export function isHighlighted(range: Range, host: HTMLElement): boolean {
  if (markOf(range.startContainer, host) && markOf(range.endContainer, host) === markOf(range.startContainer, host)) return true
  const walker = document.createTreeWalker(host, NodeFilter.SHOW_TEXT)
  let any = false
  for (let n = walker.nextNode() as Text | null; n; n = walker.nextNode() as Text | null) {
    if (!range.intersectsNode(n) || !n.data.trim()) continue
    if (n === range.endContainer && range.endOffset === 0) continue
    if (n === range.startContainer && range.startOffset === n.data.length) continue
    any = true
    if (!markOf(n, host)) return false
  }
  return any
}

/** Bold or italic the selection (the browser's command, so Cmd/Ctrl+Z undoes it). */
export function toggleInline(name: 'bold' | 'italic'): void {
  try {
    document.execCommand('styleWithCSS', false, 'false')
    document.execCommand(name)
  } catch {
    /* no editing commands here (tests) */
  }
}

/**
 * Highlight the selected words in `host`, or take the highlight off when
 * they're all highlighted already. Leaves the same words selected.
 */
export function toggleHighlight(range: Range, host: HTMLElement): void {
  const on = isHighlighted(range, host)
  // Work on whole marks at the edges, so taking one off doesn't leave half of it.
  const startMark = markOf(range.startContainer, host)
  const endMark = markOf(range.endContainer, host)
  if (startMark) range.setStartBefore(startMark)
  if (endMark) range.setEndAfter(endMark)
  const frag = range.extractContents()
  frag.querySelectorAll('mark').forEach((m) => m.replaceWith(...Array.from(m.childNodes)))
  let inserted: Node
  if (on) {
    inserted = frag
  } else {
    const mark = document.createElement('mark')
    mark.appendChild(frag)
    inserted = mark
  }
  const first = inserted.firstChild
  const last = inserted.lastChild
  const wrapper = inserted.nodeType === Node.ELEMENT_NODE ? inserted : null
  range.insertNode(inserted)
  host.normalize()
  const sel = window.getSelection()
  if (!sel) return
  const after = document.createRange()
  if (wrapper) after.selectNodeContents(wrapper)
  else if (first && last && host.contains(first) && host.contains(last)) {
    after.setStartBefore(first)
    after.setEndAfter(last)
  } else return
  sel.removeAllRanges()
  sel.addRange(after)
}

/** Tell the block its html changed, the way typing does (useBlockEditor saves it on input). */
export function announceInput(host: HTMLElement): void {
  host.dispatchEvent(new Event('input', { bubbles: true }))
}
